/**
 * DOM-overlay label system for PlayCanvas world.
 *
 * computeLabelScreenPos — pure projection helper, exported for tests.
 * resolveInteract — pure [E]-target resolution (pending question > inspector;
 * nearest wins between desk and gallery board), exported for tests.
 *
 * LabelSystem — projects station world positions to screen each frame and
 * drives absolutely-placed DOM divs (run title, status+duration, phase list,
 * proximity transcript tail ≤3 within 8u), plus mini-cards for the gallery
 * wall boards (title, status pill, relative time within ~10u). Also handles
 * the [E] proximity prompt ("Answer" for a pending interaction, "Inspect"
 * to open the run inspector) and E-key interaction wiring.
 */
import * as pc from 'playcanvas'
import { useArenaStore } from '../../store'
import type { NormalizedRun, TranscriptEvent } from '../../api/types'
import { injectWorldCardStyles, labelTierFor, phaseSegments } from './labelStyles'
import type { GalleryBoard } from './gallery'
import { relativeTime } from './gallery'
import { INPUT_PRIORITY, modalInputRouter } from '../../input/modalInputRouter'
import {
  createWorldOverlay,
  escapeWorldHtml,
  type OverlayHandle,
} from './interiorShared'
import {
  getColliders,
  hasClearSightline,
  type AABB,
} from '../colliders'
import { computeLabelScreenPos, type ScreenPos } from './worldProjection'
export { computeLabelScreenPos, type ScreenPos } from './worldProjection'

const PROXIMITY_DIST = 8   // show transcript tail within this distance
export const INTERACT_DIST = 2.5 // show [E] prompt within this distance
const GALLERY_CARD_DIST = 10 // show gallery board mini-cards within this distance
const TRANSCRIPT_TAIL = 3
const LABEL_HEIGHT_OFFSET = 3.6 // world-units above station origin
const BOARD_LABEL_OFFSET = 0.55 // world-units above a gallery board centre
export const WORLD_LABEL_FLOOR_TOLERANCE = 2.5

const STATUS_COLORS: Record<string, string> = {
  completed: '#22d3ee',
  running:   '#a78bfa',
  queued:    '#94a3b8',
  failed:    '#f87171',
  cancelled: '#6b7280',
}


function formatDuration(ms: number): string {
  const s = Math.floor(ms / 1000)
  const m = Math.floor(s / 60)
  return `${m}m ${String(s % 60).padStart(2, '0')}s`
}

// ── Pure projection helper ─────────────────────────────────────────────────

// ── Pure [E]-target resolution ─────────────────────────────────────────────

/** A candidate interact target within INTERACT_DIST. */
export interface NearTarget {
  runId: string
  dist: number
}

export type InteractResolution =
  | { action: 'answer'; runId: string }
  | { action: 'inspect'; runId: string; target: 'desk' | 'board' }
  | null

/**
 * Resolves what [E] does. A desk with a pending interaction always wins
 * (existing behavior — answering takes priority over inspecting). Otherwise
 * the nearest target wins between the nearest desk and the nearest gallery
 * board, opening the run inspector. All inputs are pre-filtered to
 * INTERACT_DIST by the caller; null means no such target.
 */
export function resolveInteract(
  pendingDesk: NearTarget | null,
  nearestDesk: NearTarget | null,
  nearestBoard: NearTarget | null,
): InteractResolution {
  if (pendingDesk) return { action: 'answer', runId: pendingDesk.runId }
  if (nearestDesk && nearestBoard) {
    return nearestDesk.dist <= nearestBoard.dist
      ? { action: 'inspect', runId: nearestDesk.runId, target: 'desk' }
      : { action: 'inspect', runId: nearestBoard.runId, target: 'board' }
  }
  if (nearestDesk) return { action: 'inspect', runId: nearestDesk.runId, target: 'desk' }
  if (nearestBoard) return { action: 'inspect', runId: nearestBoard.runId, target: 'board' }
  return null
}

/** Shared visibility gate for projected run cards, boards, and their E targets. */
export function shouldShowWorldAffordance(
  roomActive: boolean,
  camera: readonly [number, number, number],
  target: readonly [number, number, number],
  maxDistance: number,
  colliders: readonly AABB[] = [],
  targetHeight = 0,
): boolean {
  if (!roomActive) return false
  if (Math.abs(camera[1] - target[1]) > WORLD_LABEL_FLOOR_TOLERANCE) return false
  if (Math.hypot(camera[0] - target[0], camera[2] - target[2]) > maxDistance) {
    return false
  }
  return hasClearSightline(
    { x: camera[0], y: camera[1], z: camera[2] },
    { x: target[0], y: target[1] + targetHeight, z: target[2] },
    colliders,
  )
}

// ── DOM label system ──────────────────────────────────────────────────────

export class LabelSystem {
  private cameraEntity!: pc.Entity
  private container!: HTMLDivElement
  private overlay: OverlayHandle | null = null
  private disposeInput?: () => void
  private labels = new Map<string, HTMLDivElement>()
  private boardLabels = new Map<string, HTMLDivElement>()
  private tmpVec!: pc.Vec3
  private currentResolution: InteractResolution = null

  private readonly onKeyDown = (e: KeyboardEvent): boolean => {
    if (e.code !== 'KeyE') return false
    const state = useArenaStore.getState()
    if (!state.isPlaying) return false
    const res = this.currentResolution
    if (!res) return false
    if (res.action === 'answer') {
      const interactions = state.interactionsByRun[res.runId] ?? []
      const pending = interactions.find((i) => !i.respondedAt)
      if (!pending) return false
      state.setActiveInteraction(pending)
      document.exitPointerLock()
    } else {
      state.openInspector(res.runId)
      // Only release the pointer if the panel actually opened (gating passed).
      if (useArenaStore.getState().inspectorRunId === res.runId) {
        document.exitPointerLock()
      }
    }
    return true
  }

  init(app: pc.Application, cameraEntity: pc.Entity): void {
    this.cameraEntity = cameraEntity
    this.tmpVec = new pc.Vec3()

    injectWorldCardStyles()
    this.overlay = createWorldOverlay(app)
    this.container = this.overlay.element
    this.disposeInput = modalInputRouter.register(INPUT_PRIORITY.world, this.onKeyDown)
  }

  /** Called each frame. Updates DOM label positions and content. */
  frame(
    positions: Map<string, [number, number, number]>,
    boards: readonly GalleryBoard[] = [],
    roomActive = true,
  ): void {
    const state = useArenaStore.getState()
    if (this.overlay) this.overlay.element.style.display = roomActive ? 'block' : 'none'
    if (!roomActive) {
      this.currentResolution = null
      for (const div of this.labels.values()) div.style.display = 'none'
      for (const div of this.boardLabels.values()) div.style.display = 'none'
      if (state.nearWaitingStation) state.setNearWaitingStation(false)
      if (state.nearestStationRunId !== null) state.setNearestStationRunId(null)
      return
    }
    const { runs, transcriptsByRun, interactionsByRun } = state
    const camComp = this.cameraEntity.camera
    if (!camComp) return

    const camPos = this.cameraEntity.getPosition()
    const camera: readonly [number, number, number] = [camPos.x, camPos.y, camPos.z]
    const colliders = getColliders()
    const runById = new Map(runs.map((r) => [r.id, r]))

    // ── Pass 1: gather desk-station geometry + proximity candidates ────────
    interface DeskFrame {
      run: NormalizedRun
      screenPos: ScreenPos
      dist: number
      hasPending: boolean
    }
    const deskFrames = new Map<string, DeskFrame>()

    let pendingDesk: NearTarget | null = null
    let nearestDesk: NearTarget | null = null
    let nearestStationRunId: string | null = null
    let nearestStationDist = Infinity

    const visibleRunIds = new Set(positions.keys())

    for (const run of runs) {
      const pos = positions.get(run.id)
      if (!pos) {
        this.getOrCreate(run.id).style.display = 'none'
        continue
      }

      const dx = camPos.x - pos[0]
      const dz = camPos.z - pos[2]
      const dist = Math.sqrt(dx * dx + dz * dz)
      if (!shouldShowWorldAffordance(
        true,
        camera,
        pos,
        20,
        colliders,
        2,
      )) {
        this.getOrCreate(run.id).style.display = 'none'
        continue
      }

      // Project label anchor (above station) to screen
      const screenPos = computeLabelScreenPos(
        pos[0], pos[1] + LABEL_HEIGHT_OFFSET, pos[2],
        (wv, out) => camComp.worldToScreen(wv, out),
        this.tmpVec,
      )

      if (!screenPos) {
        this.getOrCreate(run.id).style.display = 'none'
        continue
      }

      const interactions = interactionsByRun[run.id] ?? []
      const hasPending = interactions.some((i) => !i.respondedAt)

      if (dist < INTERACT_DIST) {
        if (hasPending && (!pendingDesk || dist < pendingDesk.dist)) {
          pendingDesk = { runId: run.id, dist }
        }
        if (!nearestDesk || dist < nearestDesk.dist) {
          nearestDesk = { runId: run.id, dist }
        }
      }

      if (dist < PROXIMITY_DIST && dist < nearestStationDist) {
        nearestStationDist = dist
        nearestStationRunId = run.id
      }

      deskFrames.set(run.id, { run, screenPos, dist, hasPending })
    }

    // ── Gallery boards: proximity candidates ───────────────────────────────
    interface BoardFrame {
      board: GalleryBoard
      run: NormalizedRun
      screenPos: ScreenPos
      dist: number
    }
    const boardFrames: BoardFrame[] = []
    let nearestBoard: NearTarget | null = null

    for (const board of boards) {
      const run = runById.get(board.runId)
      if (!run) continue

      const dx = camPos.x - board.x
      const dz = camPos.z - board.z
      const dist = Math.sqrt(dx * dx + dz * dz)

      if (!shouldShowWorldAffordance(
        true,
        camera,
        [board.x, board.y, board.z],
        GALLERY_CARD_DIST,
        colliders,
        BOARD_LABEL_OFFSET,
      )) continue

      const screenPos = computeLabelScreenPos(
        board.x, board.y + BOARD_LABEL_OFFSET, board.z,
        (wv, out) => camComp.worldToScreen(wv, out),
        this.tmpVec,
      )
      if (!screenPos) continue

      if (dist < INTERACT_DIST && (!nearestBoard || dist < nearestBoard.dist)) {
        nearestBoard = { runId: board.runId, dist }
      }
      boardFrames.push({ board, run, screenPos, dist })
    }

    // ── Resolve the [E] target: pending question > inspect; nearest wins ───
    const resolution = resolveInteract(pendingDesk, nearestDesk, nearestBoard)
    this.currentResolution = resolution

    // ── Pass 2: render desk cards ──────────────────────────────────────────
    for (const [runId, f] of deskFrames) {
      const div = this.getOrCreate(runId)
      div.style.display    = 'block'
      div.style.left       = `${f.screenPos.x}px`
      div.style.top        = `${f.screenPos.y}px`
      div.style.transform  = 'translate(-50%, -100%)'

      let prompt: 'answer' | 'inspect' | null = null
      if (f.hasPending && f.dist < INTERACT_DIST) {
        prompt = 'answer'
      } else if (
        resolution?.action === 'inspect' &&
        resolution.target === 'desk' &&
        resolution.runId === runId
      ) {
        prompt = 'inspect'
      }

      div.innerHTML = buildLabelHtml(
        f.run,
        transcriptsByRun[runId] ?? [],
        f.dist,
        prompt,
      )
    }

    // Hide labels for runs no longer in positions
    for (const [runId, div] of this.labels) {
      if (!visibleRunIds.has(runId)) div.style.display = 'none'
    }

    // ── Render gallery board mini-cards ────────────────────────────────────
    const visibleBoardRunIds = new Set<string>()
    for (const f of boardFrames) {
      visibleBoardRunIds.add(f.board.runId)
      const div = this.getOrCreateBoard(f.board.runId)
      div.style.display    = 'block'
      div.style.left       = `${f.screenPos.x}px`
      div.style.top        = `${f.screenPos.y}px`
      div.style.transform  = 'translate(-50%, -100%)'

      const showInteract =
        resolution?.action === 'inspect' &&
        resolution.target === 'board' &&
        resolution.runId === f.board.runId
      div.innerHTML = buildBoardCardHtml(f.run, showInteract)
    }
    for (const [runId, div] of this.boardLabels) {
      if (!visibleBoardRunIds.has(runId)) div.style.display = 'none'
    }

    // ── Store sync ─────────────────────────────────────────────────────────
    const store = useArenaStore.getState()
    const isNear = pendingDesk !== null
    if (isNear !== store.nearWaitingStation) {
      store.setNearWaitingStation(isNear)
    }
    if (nearestStationRunId !== store.nearestStationRunId) {
      store.setNearestStationRunId(nearestStationRunId)
    }
  }

  private getOrCreate(runId: string): HTMLDivElement {
    let div = this.labels.get(runId)
    if (!div) {
      div = this.createLabelDiv()
      this.labels.set(runId, div)
    }
    return div
  }

  private getOrCreateBoard(runId: string): HTMLDivElement {
    let div = this.boardLabels.get(runId)
    if (!div) {
      div = this.createLabelDiv()
      this.boardLabels.set(runId, div)
    }
    return div
  }

  private createLabelDiv(): HTMLDivElement {
    const div = document.createElement('div')
    Object.assign(div.style, {
      position:   'absolute',
      fontFamily: 'ui-monospace, monospace',
      fontSize:   '12px',
      lineHeight: '1.4',
      whiteSpace: 'nowrap',
      userSelect: 'none',
    })
    this.container.appendChild(div)
    return div
  }

  destroy(): void {
    this.disposeInput?.()
    this.disposeInput = undefined
    this.overlay?.dispose()
    this.overlay = null
    this.labels.clear()
    this.boardLabels.clear()
  }
}

// ── Label HTML builders ───────────────────────────────────────────────────

function buildLabelHtml(
  run: NormalizedRun,
  transcripts: TranscriptEvent[],
  dist: number,
  prompt: 'answer' | 'inspect' | null,
): string {
  const tier = labelTierFor(dist)
  if (tier === 'hidden') return ''

  const color = STATUS_COLORS[run.status] ?? '#94a3b8'
  const isTerminal = run.status === 'completed' || run.status === 'failed'
  const createdMs = new Date(run.createdAt).getTime()
  const endMs = isTerminal ? new Date(run.updatedAt).getTime() : Date.now()
  const duration = formatDuration(endMs - createdMs)

  // Minimal chip tier: status dot + truncated title only.
  if (tier === 'chip') {
    return (
      `<div class="wchip">` +
      `<span class="wchip-dot" style="background:${color};"></span>` +
      `<span class="wchip-title">${escapeWorldHtml(run.title)}</span>` +
      `</div>`
    )
  }

  // Full card tier.
  let html = `<div class="wcard">`
  html += `<div class="wcard-title">${escapeWorldHtml(run.title)}</div>`
  html += `<div class="wcard-meta">`
  html += `<span class="wcard-pill wcard-pill--${escapeWorldHtml(run.status)}">${escapeWorldHtml(run.status.toUpperCase())}</span>`
  html += `<span>${escapeWorldHtml(duration)}</span>`
  if (run.activeAgents > 0) html += `<span>${run.activeAgents}&#9889;</span>`
  html += `</div>`

  // Phase progress bar: one segment per phase (done/active/todo/failed).
  if (run.phases.length > 0) {
    html += `<div class="wcard-bar">`
    for (const seg of phaseSegments(run.phases)) {
      html += `<span class="wcard-seg wcard-seg--${seg}"></span>`
    }
    html += `</div>`
  }

  // Proximity transcript tail — terminal-styled card.
  if (dist < PROXIMITY_DIST) {
    const tail = transcripts
      .filter((e) => e.type === 'text' || e.type === 'tool_call' || e.type === 'tool_info' || e.type === 'tool_result')
      .slice(-TRANSCRIPT_TAIL)
    if (tail.length > 0) {
      html += `<div class="wcard-term">`
      for (const e of tail) {
        const isTool = e.type === 'tool_call' || e.type === 'tool_info'
        const prefix = e.type === 'tool_call' ? '&#9881; ' : e.type === 'tool_info' ? '&#9656; ' : e.type === 'tool_result' ? '&#8592; ' : ''
        const body = isTool
          ? (() => { try { return (JSON.parse(e.content) as { name?: string }).name ?? e.content } catch { return e.content } })()
          : e.content.slice(0, 60)
        html += `<div${isTool ? ' class="t-tool"' : ''}>${prefix}${escapeWorldHtml(body)}</div>`
      }
      html += `</div>`
    }
  }

  // [E] prompt — keycap style. "Answer" for a pending interaction,
  // "Inspect" to open the run inspector.
  if (prompt !== null) {
    html += `<div class="wcard-kbd-row"><span class="wcard-kbd">E</span> ${prompt === 'answer' ? 'Answer' : 'Inspect'}</div>`
  }

  html += `</div>`
  return html
}

/** Gallery-board mini-card: title, status pill, relative time (+ [E] Inspect). */
function buildBoardCardHtml(run: NormalizedRun, showInteract: boolean): string {
  let html = `<div class="wcard">`
  html += `<div class="wcard-title">${escapeWorldHtml(run.title)}</div>`
  html += `<div class="wcard-meta">`
  html += `<span class="wcard-pill wcard-pill--${escapeWorldHtml(run.status)}">${escapeWorldHtml(run.status.toUpperCase())}</span>`
  html += `<span>${escapeWorldHtml(relativeTime(run.createdAt))}</span>`
  html += `</div>`
  if (showInteract) {
    html += `<div class="wcard-kbd-row"><span class="wcard-kbd">E</span> Inspect</div>`
  }
  html += `</div>`
  return html
}
